import { useCallback, useMemo, useState } from 'react'

type ModalState<T extends string> = Record<T, boolean>

type UseModalManagerOptions<T extends string> = {
  onClose?: (modalId: T) => void
}

export function useModalManager<T extends string, U = void>(
  initialState: T[],
  options?: UseModalManagerOptions<T>
) {
  const [selectedData, setSelectedData] = useState<U>()

  const initialModalState: ModalState<T> = useMemo(
    () =>
      initialState.reduce((acc, modalId) => ({ ...acc, [modalId]: false }), {} as ModalState<T>),
    [initialState]
  )

  const [modalState, setModalState] = useState<ModalState<T>>(initialModalState)

  const toggleModal = useCallback((modalId: T, data?: U, closeOtherModals?: boolean) => {
    setSelectedData(data)

    if (closeOtherModals) {
      setModalState((prevState) => {
        return Object.keys(prevState).reduce((acc, key) => {
          return { ...acc, [key]: key === modalId }
        }, {} as ModalState<T>)
      })

      return
    }

    setModalState((prevState) => {
      if (!prevState) return prevState

      return {
        ...prevState,
        [modalId]: !prevState[modalId]
      }
    })
  }, [])

  const closeModel = useCallback(
    (modalId: T) => {
      setModalState((prevState) => {
        if (!prevState) return prevState

        return {
          ...prevState,
          [modalId]: false
        }
      })

      options?.onClose?.(modalId)
    },
    [options]
  )

  const isOpen = useCallback(
    (modalId: T) => {
      return modalState[modalId]
    },
    [modalState]
  )

  const closeAll = useCallback(() => {
    setModalState(initialModalState)
  }, [initialModalState])

  // Backward-compatible API (closeModel) + friendly alias (closeModal)
  const closeModal = closeModel

  return { selectedData, modalState, isOpen, toggleModal, closeModel, closeModal, closeAll }
}
